import { instrumentTag, pairParts, type AssetClass } from '@platform/shared';
import { cx } from './ui';

/** Text indices to highlight for `query`, matched on letters and digits only ("eurusd" marks "EUR/USD" across the slash). */
function marked(text: string, query: string): Set<number> {
  const q = query.toUpperCase().replace(/[^A-Z0-9]/g, '');
  const at: number[] = [];
  let flat = '';
  [...text.toUpperCase()].forEach((ch, i) => {
    if (/[A-Z0-9]/.test(ch)) {
      flat += ch;
      at.push(i);
    }
  });
  const k = q ? flat.indexOf(q) : -1;
  return k < 0 ? new Set() : new Set(at.slice(k, k + q.length));
}

/** `text` with the part matching `query` highlighted. */
export function Marked({ text, query }: { text: string; query?: string }) {
  const on = query ? marked(text, query) : new Set<number>();
  if (!on.size) return <>{text}</>;
  const first = Math.min(...on);
  const last = Math.max(...on) + 1;
  return (
    <>
      {text.slice(0, first)}
      <mark className="rounded-[2px] bg-warn-bg text-inherit">{text.slice(first, last)}</mark>
      {text.slice(last)}
    </>
  );
}

/**
 * An instrument's name as traders read it: "BTC" with a muted "/USDT", "EUR/USD", or "GOLD" with its market code
 * ("XAU/USD") as a tag. Gold, stocks and indices carry no quote currency.
 */
export function PairName({ symbol, assetClass, tag = true, query, className }: { symbol: string; assetClass?: AssetClass; tag?: boolean; query?: string; className?: string }) {
  const [base, quote] = pairParts(symbol, assetClass);
  const fx = quote && quote !== 'USDT';
  const code = tag ? instrumentTag(symbol) : null;
  return (
    <span className={cx('inline-flex min-w-0 items-center gap-1.5 whitespace-nowrap', className)}>
      <span>
        {fx ? (
          <Marked text={`${base}/${quote}`} query={query} />
        ) : (
          <>
            <Marked text={base} query={query} />
            {quote && <span className="font-medium text-muted">/{quote}</span>}
          </>
        )}
      </span>
      {code && (
        <span className="rounded border border-line-strong px-1 font-mono text-[10px] leading-[15px] font-medium tracking-normal text-muted">
          <Marked text={code} query={query} />
        </span>
      )}
    </span>
  );
}
